import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSmile, FiMeh, FiFrown, FiTrendingUp, FiMinus, FiClock } = FiIcons;

const HappinessIndex = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamAverage, setTeamAverage] = useState(null);
  const [userRating, setUserRating] = useState(null);
  const [existingRecordId, setExistingRecordId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.teamId) {
      loadHappinessData();
    }
  }, [user]);

  const loadHappinessData = async () => {
    try {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];

      // 1. Get Team Members
      const teamRelations = await apiService.getUserTeamRelations({ 
        team_id: user.teamId,
        limit: 1000 
      });
      
      const memberIds = teamRelations.data 
        ? teamRelations.data.map(m => m.user_id) 
        : [];
      
      if (user.id && !memberIds.includes(parseInt(user.id))) {
        memberIds.push(parseInt(user.id));
      }

      if (memberIds.length === 0) {
        setLoading(false);
        return;
      }

      // 2. Fetch Happiness Indices for all team members (not just today)
      // We fetch a larger limit to ensure we get recent history
      const happinessResponse = await apiService.getHappinessIndices({
        'user_id[in]': memberIds.join(','),
        limit: 2000 // Increased limit to catch older records if necessary
      });

      const allRecords = happinessResponse.data || [];
      
      // 3. Process records to find the LATEST entry for each user
      const latestRecordsByUser = {};

      allRecords.forEach(record => {
        const userId = record.user_id;
        const currentBest = latestRecordsByUser[userId];

        // Ensure we have a date to compare
        const recordDate = record.evaluation_date || '0000-00-00';
        
        if (!currentBest) {
          latestRecordsByUser[userId] = record;
        } else {
          const bestDate = currentBest.evaluation_date || '0000-00-00';
          // If this record is newer, OR same date but higher ID (created later)
          if (recordDate > bestDate || (recordDate === bestDate && record.id > currentBest.id)) {
            latestRecordsByUser[userId] = record;
          }
        }
      });

      // 4. Calculate Team Average based on LATEST values
      const latestValues = Object.values(latestRecordsByUser).map(r => r.value);
      
      if (latestValues.length > 0) {
        const sum = latestValues.reduce((acc, val) => acc + val, 0);
        const avg = sum / latestValues.length;
        setTeamAverage(Math.round(avg * 10) / 10);
      } else {
        setTeamAverage(null);
      }

      // 5. Determine User's State
      const myLatestRecord = latestRecordsByUser[parseInt(user.id)];
      
      if (myLatestRecord) {
        // Always show the last value selected (even if it was yesterday)
        setUserRating(myLatestRecord.value);

        // check if the record is from TODAY
        const recordDate = myLatestRecord.evaluation_date || '';
        if (recordDate === today) {
           // It's today's record -> we will update it if clicked again
           setExistingRecordId(myLatestRecord.id);
        } else {
           // It's an old record -> we will create a NEW one if clicked
           setExistingRecordId(null);
        }
      } else {
        setUserRating(null);
        setExistingRecordId(null);
      }

    } catch (error) {
      console.error('Error loading happiness data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRating = async (value) => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      setUserRating(value); // Optimistic update
      
      const today = new Date().toISOString().split('T')[0];
      
      if (existingRecordId) {
        // Update existing record (because it is from today)
        await apiService.updateHappinessIndex(existingRecordId, {
          value: value,
          evaluation_date: today,
          user_id: parseInt(user.id)
        });
      } else {
        // Create new record (either first ever, or new day)
        const response = await apiService.createHappinessIndex({
          user_id: parseInt(user.id),
          value: value,
          evaluation_date: today
        });
        
        if (response && response.id) {
          setExistingRecordId(response.id);
        }
      }
      
      // Reload to update average
      loadHappinessData();
      
    } catch (error) {
      console.error('Error saving happiness rating:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getSmileIcon = (score, sizeClass = "w-8 h-8") => {
    if (score >= 6) return <SafeIcon icon={FiSmile} className={`${sizeClass} text-green-500`} />;
    if (score >= 4) return <SafeIcon icon={FiMeh} className={`${sizeClass} text-yellow-500`} />;
    return <SafeIcon icon={FiFrown} className={`${sizeClass} text-red-500`} />;
  };

  const ratingOptions = [
    { value: 1, color: 'bg-red-500', label: 'Sehr schlecht', icon: FiFrown },
    { value: 2, color: 'bg-red-400', label: 'Schlecht', icon: FiFrown },
    { value: 3, color: 'bg-orange-400', label: 'Eher schlecht', icon: FiMeh },
    { value: 4, color: 'bg-yellow-400', label: 'Neutral', icon: FiMeh },
    { value: 5, color: 'bg-yellow-300', label: 'Eher gut', icon: FiSmile },
    { value: 6, color: 'bg-green-400', label: 'Gut', icon: FiSmile },
    { value: 7, color: 'bg-green-500', label: 'Sehr gut', icon: FiSmile },
  ];

  if (loading) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
        
        {/* Left: Team Average */}
        <div className="flex items-center gap-4 w-full md:w-auto md:border-r md:border-gray-100 md:pr-8">
          <div className="p-2 bg-brand-primary/10 rounded-full">
            <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-4">
               <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">Team Stimmung</h3>
               <Link 
                 to="/happiness-history"
                 className="text-[10px] text-brand-secondary hover:text-brand-dark flex items-center font-medium transition-colors"
               >
                 <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                 Verlauf
               </Link>
            </div>
            <div className="flex items-center gap-3">
              {teamAverage !== null ? (
                <>
                  <div className="transform scale-110 origin-left">
                    {getSmileIcon(teamAverage, "w-8 h-8")}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] text-gray-400">Ø Score</span>
                    <span className="text-base font-bold text-brand-dark leading-none">
                      {teamAverage}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center text-gray-400">
                  <SafeIcon icon={FiMinus} className="w-6 h-6" />
                  <span className="ml-2 text-xs">Noch keine Daten</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: User Input */}
        <div className="flex flex-col items-center md:items-end w-full md:w-auto">
          <h3 className="text-xs font-medium text-gray-500 mb-2">Wie fühlst du dich heute?</h3>
          <div className="flex gap-1.5">
            {ratingOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleRating(option.value)}
                disabled={submitting}
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 relative
                  ${userRating === option.value 
                    ? 'ring-2 ring-offset-2 ring-brand-primary/30 transform scale-110 opacity-100 shadow-md' 
                    : 'hover:scale-110 opacity-40 hover:opacity-100 grayscale hover:grayscale-0'}
                  ${option.color}
                `}
                title={option.label}
              >
                <SafeIcon icon={option.icon} className="text-white w-5 h-5" />
                {userRating === option.value && (
                  <span className="absolute -bottom-6 text-[10px] text-brand-dark font-medium whitespace-nowrap bg-white px-1.5 py-0.5 rounded shadow-sm z-10 border border-gray-100">
                    {option.label}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default HappinessIndex;